import { useState } from 'react';
import Dotdotdot from 'react-dotdotdot';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Divider } from '@hivebrite/design-system/Divider';
import { PillsList } from '@hivebrite/design-system/PillsList';
import { useTheme, useBreakpoint } from '@hivebrite/design-system/Theme';
import type { UseBreakpointProps } from '@hivebrite/design-system/Theme';

import type { PublicationLayoutsProps } from 'hb-builder/components/BuilderComponents/dynamic/Publication/PublicationLayouts';
import type { Event } from 'hb-builder/components/BuilderComponents/dynamic/types/event';
import PublicationCommonLoader from 'hb-builder/components/common/LoadingComponents/PublicationCommonLoader';
import { goToModuleDetailPage } from 'hb-builder/utils/goToModuleDetailPage';
import Attendees from 'hb-react/shared/components/DesignSystem/Attendees';
import ClampedLinkWorkaround from 'hb-react/shared/components/DesignSystem/ClampedLinkWorkaround';
import EventDate from 'hb-react/shared/components/EventDate';
import PublicationText from 'hb-react/shared/components/Publication/PublicationText';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';
import Info from 'hb-react/shared/icons/Info';

export type EventsWrapperProps = {
  data: Event;
  isBackOffice: boolean;
  isLoading: boolean;
} & Omit<PublicationLayoutsProps, 'app' | 'isPublic' | 'type'>;

const MOBILE_CONTENT_MARGIN = 16;

const EventsWrapper = ({
  data: fetchedEvent,
  dynamicAttributes,
  ids,
  isBackOffice,
  isLoading,
  navigate,
  whiteLabelUrl,
}: EventsWrapperProps) => {
  const breakpoint = useBreakpoint();
  const theme = useTheme();
  const timeZoneConfiguration = useTimeZoneConfiguration();
  const [isHover, setIsHover] = useState(false);

  if (isLoading) {
    return <PublicationCommonLoader />;
  }

  const {
    id,
    attending_users: attendingUsers,
    has_attendees: hasAttendees,
    title,
    description,
    cover_picture: coverPicture,
    locations,
    network_categories: tags,
    total_attendees: totalAttendees,
    total_external_attendees: totalExternalAttendees,
    webinar_url: webinarUrl,
  } = fetchedEvent;

  const {
    displayAttendees,
    displayCoverPicture,
    displayTitle,
    displayCategories,
    displayContent,
    displayLocation,
    displayOnlineEvent,
    displayDate,
  } = dynamicAttributes?.fields || {};

  const noCategories = !displayCategories || tags?.length === 0;
  // get mobile margin to display img in all the width.

  return (
    <WrapperStyled>
      <WrapperHeader>
        {displayCoverPicture && (
          <ImgWrapper
            data-testid="builder-cover-wrapper"
            mx={breakpoint === 's' ? `-${MOBILE_CONTENT_MARGIN}px` : 'unset'}
            onClick={() =>
              ids &&
              navigate &&
              goToModuleDetailPage({
                event: fetchedEvent,
                ids,
                isBackOffice,
                navigate,
                whiteLabelUrl,
              })
            }
            onMouseEnter={() => setIsHover(true)}
            onMouseLeave={() => setIsHover(false)}
            $breakpoint={breakpoint}
          >
            <StyledImg
              as="img"
              src={coverPicture}
              alt="cover event"
              $isHover={isHover}
            />
          </ImgWrapper>
        )}
        <HeaderContent>
          {displayTitle && (
            <Title
              onClick={() =>
                ids &&
                navigate &&
                goToModuleDetailPage({
                  event: fetchedEvent,
                  ids,
                  isBackOffice,
                  navigate,
                  whiteLabelUrl,
                })
              }
              onMouseEnter={() => setIsHover(true)}
              onMouseLeave={() => setIsHover(false)}
            >
              <ClampedLinkWorkaround
                clamp={2}
                lineHeight=""
                margin={-2}
                padding={2}
                useNativeClamp
              >
                {title}
              </ClampedLinkWorkaround>
            </Title>
          )}

          <EventDetails>
            {displayDate && (
              <Box as="p" mb="5px" display="block">
                <BoldedSpan>{__('Date')}: </BoldedSpan>
                <EventDate
                  event={{
                    start_date: fetchedEvent.start_date_timestamp,
                    end_date: fetchedEvent.end_date_timestamp,
                    timezone: fetchedEvent.timezone,
                  }}
                  as="span"
                  timezoneConfiguration={timeZoneConfiguration}
                />
              </Box>
            )}
            {locations?.address && displayLocation && (
              <Box mb="5px">
                <Dotdotdot clamp={2}>
                  <BoldedSpan>{__('Location')}: </BoldedSpan>
                  {locations.address}
                </Dotdotdot>
              </Box>
            )}
            {webinarUrl && displayOnlineEvent && (
              <Box as="p" mb={0} alignItems="end">
                {__('Online event')}
                <Box as="span" pl={1}>
                  <Info fill={theme.colors.primary} height={16} width={16} />
                </Box>
              </Box>
            )}
            {displayAttendees &&
              (hasAttendees || attendingUsers?.length > 0) && (
                <Box mt={3}>
                  <Attendees
                    eventType="network_events_event"
                    attendees={attendingUsers}
                    maxAvatars={4}
                    eventId={id}
                    imgSize={40}
                    totalAttendees={totalAttendees}
                    totalExternalAttendees={totalExternalAttendees}
                    lazy={!!hasAttendees}
                  />
                </Box>
              )}
          </EventDetails>
        </HeaderContent>
      </WrapperHeader>
      {!noCategories && (
        <Box mt={3}>
          <PillsList
            pills={tags}
            seeLessLabel={s__('Tags|See less')}
            seeMoreLabel={s__('Tags|See more')}
          />
        </Box>
      )}

      <ContentWrapper>
        {displayContent && (
          <>
            <Divider mt={3} mb={4} />
            <PublicationText text={description} />
          </>
        )}
      </ContentWrapper>
    </WrapperStyled>
  );
};

export default EventsWrapper;

const WrapperStyled = styled(Box).attrs(() => ({
  mt: {
    m: 4,
    l: 6,
  },
  flexDirection: 'column',
}))``;

/**
 * Header section
 */

const WrapperHeader = styled(Box).attrs(() => ({
  flexDirection: {
    _: 'column',
    l: 'row',
  },
}))``;
const HeaderContent = styled(Box).attrs(() => ({
  mt: {
    _: 4,
    l: 0,
  },
  pl: {
    l: '24px',
  },
  maxWidth: {
    l: '310px',
    xl: '440px',
  },
}))`
  flex-direction: column;
`;

const Title = styled(Box).attrs({
  font: 'h1-semibold',
})`
  cursor: pointer;
  &:hover {
    text-decoration: underline;
    color: ${({ theme }) => theme.colors.link} !important;
  }
`;

const EventDetails = styled(Box).attrs(() => ({
  mt: 3,
  flexDirection: 'column',
}))``;

const BoldedSpan = styled.span`
  font-weight: bold;
`;

/**
 * Image Section
 */

const ImgWrapper = styled(Box).attrs({
  overflow: 'hidden',
  width: {
    s: `calc(100% + ${MOBILE_CONTENT_MARGIN * 2}px)`,
    l: '468px',
    xl: '443px',
  },
})<{ $breakpoint: UseBreakpointProps }>`
  border-radius: ${({ theme, $breakpoint }) =>
    $breakpoint === 's'
      ? '0px'
      : `${theme.themeVariables.globalBorderRadius}px`};
  background: linear-gradient(rgba(0, 0, 0, 0.3), rgba(0, 0, 0, 0.6));
`;

const StyledImg = styled(Box)<{ $isHover: boolean }>`
  cursor: pointer;
  transition: transform 0.3s;
  transform: ${({ $isHover }) => ($isHover ? 'scale(1.05)' : 'scale(1)')};
  width: ${(props) => props.width || '100%'};
`;

/**
 * Content Section
 */

const ContentWrapper = styled(Box).attrs(() => ({
  color: 'primary',
  display: 'block',
  pb: { _: 6, l: 8 },
}))`
  p {
    margin-bottom: 20px;
  }
`;
